import React from "react";
import { useTranslation } from "react-i18next";
import SingleInput from "@/components/controls/SingleInput";
import Text from "@/components/ui/text";
import { monetaryValueFormatterWithDecimals } from "@/pages/deals/helpers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  distinguishCurrencySign,
  formatNumber
} from "@/utils/numbers";
import { DEFAULT_CURRENCY_CODE } from "@/utils/numberFormat";
import { getCurrentLocale } from "@/utils/locale";

interface Props {
  fieldName: string;
  label?: string;
  // Nullable so callers can render the empty-state placeholder for a metric that
  // has no value at all, instead of coercing it to a misleading 0.
  value: number | null;
  originalValue: number | null;
  contractCurrencySymbol: string;
  accountCurrencySymbol: string;
  /**
   * Null when the record has no currency recorded. Callers pass the truthful
   * value for the same reason accountCurrencyCode below does: the collision
   * test compares it against the account's, and a stood-in "USD" qualifies a
   * record that has no currency to the literal code on any $-glyph account.
   * The tooltip's own fallback is this component's business.
   */
  contractCurrencyCode: string | null | undefined;
  /**
   * Null until the account-currency setting has actually resolved. Callers must
   * pass the truthful value rather than a placeholder: the collision test below
   * compares it against the record's own currency, so standing in a guessed
   * "USD" makes a record in the account's own currency look foreign and
   * qualifies a sign that needed no qualifying. The tooltip's own fallback is
   * this component's business, not the caller's.
   */
  accountCurrencyCode: string | null;
  showConversion: boolean;
  rate?: number;
  onSave: (fieldName: string, newValue: string | number) => void;
  entityId?: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  // A real value that cannot be edited here: plain text at full colour, unlike the greyed disabled state.
  readOnly?: boolean;
  // Replaces the value tooltip while read-only, to say where the value is edited instead.
  readOnlyTooltipText?: string;
}

const ArrMrrEditableField: React.FC<Props> = ({
  fieldName,
  label,
  value,
  originalValue,
  contractCurrencySymbol,
  accountCurrencySymbol,
  contractCurrencyCode,
  accountCurrencyCode,
  showConversion,
  rate,
  onSave,
  entityId,
  disabled = false,
  disabledTooltipText,
  readOnly = false,
  readOnlyTooltipText
}) => {
  const { t } = useTranslation();
  // The two figures render as `<contract> (<account>)`, so a contract currency
  // whose symbol is the account's own glyph — `$` for CAD on a USD account —
  // leaves both halves looking like the same currency. An unresolved account
  // currency reaches distinguishCurrencySign as null, which its guard reads as
  // nothing to tell apart — so the sign stays plain until the real currency
  // lands rather than qualifying against a guess and then reverting.
  const displayCurrencySymbol = distinguishCurrencySign({
    sign: contractCurrencySymbol,
    currencyCode: contractCurrencyCode,
    accountSign: accountCurrencySymbol,
    accountCurrencyCode
  });
  // Naming a currency in the tooltip is unavoidable, so an unrecorded one
  // keeps the label callers passed before this prop went nullable.
  const contractCurrencyForDisplay =
    contractCurrencyCode ?? DEFAULT_CURRENCY_CODE;
  const formatValueWithCode = (amount: number, currencyCode: string) => {
    const { value: formattedValue } = formatNumber(
      amount,
      0,
      true,
      false,
      false,
      getCurrentLocale()
    );
    return `${currencyCode} ${formattedValue}`;
  };

  const placeholder = label ? `${t("add")} ${label}` : t("add");

  // A null originalValue means the displayed figure has no account-currency
  // amount, so no conversion is shown — the Financial Recognition panel hides
  // it in the same case and the two must not disagree. Callers passing the
  // legacy `?? 0` keep the rate-derived estimate.
  const conversionAmount =
    originalValue === null
      ? 0
      : originalValue > 0
        ? originalValue
        : rate && value != null && value > 0
          ? rate * value
          : 0;

  const getTooltipContent = () => {
    if (value == null) return placeholder;
    const contractValue = formatValueWithCode(
      value,
      contractCurrencyForDisplay
    );
    if (showConversion && conversionAmount > 0) {
      const accountValue = formatValueWithCode(
        conversionAmount,
        // Naming the currency is unavoidable here, so an unresolved setting
        // keeps the label callers passed before this prop went nullable.
        accountCurrencyCode ?? DEFAULT_CURRENCY_CODE
      );
      return `${contractValue} (${accountValue})`;
    }
    return contractValue;
  };

  const conversion = showConversion && conversionAmount > 0 && (
    <Text variant="bodySecondary">
      {`(${monetaryValueFormatterWithDecimals(conversionAmount, accountCurrencySymbol, 0)})`}
    </Text>
  );

  if (readOnly) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center gap-1 px-2 min-h-9 cursor-default"
            data-test={`${fieldName}-read-only`}
          >
            <Text variant="body">
              {value != null
                ? monetaryValueFormatterWithDecimals(
                    value,
                    displayCurrencySymbol ?? "",
                    0
                  )
                : t("unknown")}
            </Text>
            {conversion}
          </div>
        </TooltipTrigger>
        <TooltipContent enableMarkdown={false}>
          {readOnlyTooltipText ?? getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1">
          <SingleInput
            key={`${fieldName}-${entityId}-${value}`}
            fieldName={fieldName}
            initialValue={value != null ? value.toString() : ""}
            onSave={(newValue: string) =>
              onSave(
                fieldName,
                isNaN(Number(newValue)) ? newValue : Number(newValue)
              )
            }
            textVariant="body"
            isMonetary={true}
            currencySymbol={displayCurrencySymbol}
            placeholder={placeholder}
            disabled={disabled}
          />
          {conversion}
        </div>
      </TooltipTrigger>
      <TooltipContent enableMarkdown={false}>
        {disabled && disabledTooltipText
          ? disabledTooltipText
          : getTooltipContent()}
      </TooltipContent>
    </Tooltip>
  );
};

export default ArrMrrEditableField;
